import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import AccountProfile from "./AccountProfile/AccountProfile";
import AccountPassword from "./AccountPassword/AccountPassword";
import AccountPereferences from "./AccountPerferences/AccountPereferences";
import AccountLocation from "./AccountLocation/AccountLocation";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";

import { toast } from "react-toastify";
import AccountHeader from "./AccountHeader/AccountHeader";
import { setUserInf } from "../../Redux/service/Users/UsersSlice";
import trans, { isRTL } from "../../utils";
import {
  useEditUserMutation,
  useExpireAllSessionMutation,
} from "../../Redux/service/Users/AuthUsers";
import { Modal } from "react-bootstrap";
import Close from "../Helpers/CloseBtn/Close";
import Button from "../Helpers/Button/Button";
import { useGetSpecificUserQuery } from "../../Redux/service/Users/Users";
import { useNavigate } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";

function ExpireAllSessions({ active, setActive }) {
  const [logoutAllSessions, { isLoading }] = useExpireAllSessionMutation();
  const navigate = useNavigate();
  const handleConfirm = async () => {
    try {
      await logoutAllSessions().unwrap();
      navigate("/signin");
      setActive(false);
    } catch (error) {
      console.error("Expire sessions failed:", error);
      toast.error(`${error?.data?.message || "An error occurred"}`, {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
    }
  };

  return (
    <Modal show={active} centered dir={!isRTL ? "ltr" : "rtl"}>
      <Modal.Header>
        <div className="flex-between w-100">
          <Close
            style="close-32 close"
            close={() => setActive(false)}
            img="Close"
          />
        </div>
      </Modal.Header>

      <Modal.Body>
        {trans("Are you sure you want to logout from another accounts")}
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="default"
          onClick={() => setActive(false)}
          text={trans("Cancel")}
          disabled={isLoading}
        />
        <Button
          text={trans("Confirm")}
          onClick={handleConfirm}
          type="primary"
          loading={isLoading}
        />
      </Modal.Footer>
    </Modal>
  );
}

export default function Account() {
  const isMobile = useIsMobile();

  const inf = useSelector((state) => state.users.user);
  let { data, refetch } = useGetSpecificUserQuery(inf?.id, { skip: !inf?.id });
  const [submitAttempt, setSubmitAttempt] = useState(false);
  let [EditUser] = useEditUserMutation();
  let [disable, setDisabled] = useState(false);
  let [formValidation, setFormValidation] = useState(false);
  const [active, setActive] = useState(false);
  const dispatch = useDispatch();

  let initialValues = {
    name: data?.name,
    email: data?.email,
    password: null,
    confirmPassword: null,
    phone: data?.phone,
    attributes: {
      speedUnit: data?.attributes.speedUnit,
      volumeUnit: data?.attributes.volumeUnit,
      altitudeUnit: data?.attributes.altitudeUnit,
      distanceUnit: data?.attributes.distanceUnit,
      currency: data?.attributes.currency,
      timezone: data?.attributes.timezone,
    },
    latitude: data?.latitude,
    longitude: data?.longitude,
  };

  let validSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .test(
        "not-empty-or-spaces",
        trans("Name cannot be just whitespace"),
        (value) => !!value && value.trim().length > 0,
      ),
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.(com)$/,
        trans("Email must be a valid .com email address"),
      )
      .email()
      .required("Email Is Required"),
    password: Yup.string()
      .nullable()
      .test("password-rules", "", (value) => {
        if (!value) return true; // let required handle empty

        return (
          value.length >= 12 &&
          /[A-Z]/.test(value) &&
          /\d/.test(value) &&
          /[^A-Za-z0-9]/.test(value)
        );
      }),
    confirmPassword: Yup.string()
      .nullable()
      .when("password", (passwordValue, schema) => {
        const pwd = Array.isArray(passwordValue)
          ? passwordValue[0]
          : passwordValue;

        const hasPassword = typeof pwd === "string" && pwd.trim().length > 0;

        if (hasPassword) {
          return schema
            .required(trans("Confirm Password Is Required"))
            .oneOf([Yup.ref("password")], trans("Passwords do not match"));
        }

        return schema.notRequired();
      }),
    repassword: Yup.string(),
    latitude: Yup.number()
      .typeError("Latitude must be a number")
      .min(-90, "Latitude must be between -90 and 90")
      .max(90, "Latitude must be between -90 and 90")
      .nullable(),
    longitude: Yup.number()
      .typeError("Longitude must be a number")
      .min(-180, "Longitude must be between -180 and 180")
      .max(180, "Longitude must be between -180 and 180")
      .nullable(),
    attributes: Yup.object({
      speedUnit: Yup.string().required("Speed unit is required"),
      altitudeUnit: Yup.string().required("Altitude unit is required"),
      distanceUnit: Yup.string().required("Distance unit is required"),
      volumeUnit: Yup.string().required("Volume unit is required"),
    }),
  });

  const editAccount = useFormik({
    initialValues,
    validationSchema: validSchema,
    onSubmit: async (val) => {
      delete val.confirmPassword;

      try {
        await EditUser({ id: inf?.id, val: val }).unwrap();
        toast.success(trans("User Profile Edited Successfully"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
        const refreshed = await refetch(); // wait for updated data
        if (val.password) {
          setActive(true);
        }
        if (refreshed?.data) {
          dispatch(setUserInf(refreshed.data)); // dispatch new data
        }
      } catch (error) {
        editAccount.setValues(
          {
            ...editAccount.values,
            password: null,
            confirmPassword: null,
          },
          false,
        );

        editAccount.setTouched(
          {
            ...editAccount.touched,
            password: false,
            confirmPassword: false,
          },
          false,
        );

        toast.error(`${error?.data?.message || "An error occurred"}`, {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      }
    },
  });
  console.log("Edit account", editAccount);
  useEffect(() => {
    if (data) {
      editAccount.setValues(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSubmit = (e) => {
    setSubmitAttempt(true);
    editAccount.handleSubmit(e);
  };
  return (
    <div>
      <Helmet>
        <title>Account</title>
        <meta name="description" content="Account" />
      </Helmet>
      <form onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
        {!isMobile && (
          <AccountHeader
            inf={editAccount}
            submitAttempt={submitAttempt}
            disable={disable}
            formValidation={formValidation}
          />
        )}
        <div className="account">
          <AccountProfile
            inf={editAccount}
            setFormValidation={setFormValidation}
          />
          <AccountPassword
            inf={editAccount}
            disable={disable}
            setDisabled={setDisabled}
          />
          <AccountPereferences
            inf={editAccount}
            setFormValidation={setFormValidation}
          />
          <AccountLocation
            inf={editAccount}
            disable={disable}
            formValidation={formValidation}
          />
        </div>
      </form>
      <ExpireAllSessions setActive={setActive} active={active} />
    </div>
  );
}
